import React from "react";
import {Button, Space, Tag, Typography} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Link} from "react-router-dom";
import {useTranslate} from "../../../hooks/translate.hook";
import {HealthCheckerIssueDetails} from "../../../models/health-checker/health-checker";
import {formatHealthCenterDateTime} from "../health-center.helpers";

const {Text, Title} = Typography;

interface IssueHeaderProps {
    issue: HealthCheckerIssueDetails;
    loading: boolean;
    onRefresh: () => Promise<void>;
    onResolve: () => void;
}

export const IssueHeader: React.FC<IssueHeaderProps> = ({issue, loading, onRefresh, onResolve}) => {
    const t = useTranslate();
    const checkIsOk = issue.check.status === "ok";
    const canResolve = !["resolved", "closed"].includes(issue.status.toLowerCase());

    return (
        <header className="health-center-issue__header">
            <div>
                <Space align="center" wrap>
                    <Title level={4}>
                        {t("HEALTH-CENTER.ISSUE.TITLE", {
                            id: issue.id,
                            defaultValue: "Issue #{{id}}",
                        })}
                    </Title>
                    <Text code>{issue.check.checkerName}</Text>
                    <Tag
                        className="health-center-issue__check-status"
                        color={checkIsOk ? "success" : "error"}
                        icon={<FontAwesomeIcon icon={["fas", checkIsOk ? "check-circle" : "exclamation-triangle"]} />}
                    >
                        {checkIsOk
                            ? t("HEALTH-CENTER.DASHBOARD.STATUS.OK", {defaultValue: "OK"})
                            : t("HEALTH-CENTER.DASHBOARD.STATUS.NOT-OK", {defaultValue: "Not OK"})}
                    </Tag>
                </Space>
                <Text type="secondary">
                    {t("HEALTH-CENTER.ISSUE.CREATED-UPDATED", {
                        createdAt: formatHealthCenterDateTime(issue.createdAt),
                        defaultValue: "Created {{createdAt}}",
                    })}
                </Text>
            </div>
            <Space wrap>
                <Link to="/health-center/issues">
                    <Button icon={<FontAwesomeIcon icon={["fas", "arrow-left"]} />}>
                        {t("HEALTH-CENTER.ISSUE.BACK", {defaultValue: "All issues"})}
                    </Button>
                </Link>
                <Button
                    icon={<FontAwesomeIcon icon={["fas", "sync"]} />}
                    loading={loading}
                    onClick={() => void onRefresh()}
                >
                    {t("HEALTH-CENTER.ISSUE.REFRESH", {defaultValue: "Refresh"})}
                </Button>
                {canResolve ? (
                    <Button
                        type="primary"
                        icon={<FontAwesomeIcon icon={["fas", "check"]} />}
                        disabled={loading}
                        onClick={onResolve}
                    >
                        {t("HEALTH-CENTER.ISSUE.RESOLVE", {defaultValue: "Resolve"})}
                    </Button>
                ) : null}
            </Space>
        </header>
    );
};
